/**
 * @fileoverview Timesheet approval queue (MOR-906)
 *
 * Approver review surface for submitted time entries. Person is a column
 * (not a fixed grouping) and the list can be grouped by person, matter,
 * category, date, or nothing. Matter and category render as badges — the
 * category badge carries the firm's colour/icon so the taxonomy reads the
 * same as everywhere else. Clicking a row opens the full entry detail
 * (history, notes, status control); units and narrative stay editable
 * inline (§7.4 "adjust"). Approve/reject actions land in the entry's
 * audit trail AND its notes feed (reason included).
 *
 * Dependencies:
 * - Convex: timesheets.entries.approvalQueue / approveEntries /
 *   rejectEntries / adjustEntry
 */

import {
  useEffect,
  useMemo,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { format, parseISO } from "date-fns";
import { Check, Loader2, Pencil, X } from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type { TimeRoundingMode } from "@cvx/timesheets/timesheetSettings/schema";
import { roundUnits } from "@cvx/timesheets/units";
import type { FunctionReturnType } from "convex/server";

import { extractErrorMessage } from "@/utils/errors";
import { Badge, Button } from "@/ui/primitives";
import { Checkbox } from "@/ui/primitives/checkbox";
import { Input } from "@/ui/primitives/input";
import { Spinner } from "@/ui/primitives/spinner";
import { Textarea } from "@/ui/primitives/textarea";
import { Label } from "@/ui/primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import { FacetedFilter } from "@/ui/composed/filters/faceted-filter";
import { formatAud, formatAudExact } from "./format";
import { CategoryBadge } from "./CategoryBadge";
import { ApprovalScopeNote } from "./ApprovalScopeNote";
import { EntryDetailSheet } from "./EntryDetailSheet";
import { NARRATIVE_INPUT_PROPS } from "./narrativeCheck";
import { NarrativeWarningNote } from "./NarrativeWarningNote";
import { RateWorkingNote } from "./RateWorkingNote";
import { BulkActionBar, BulkActionButton } from "@/ui/composed/bulk-action-bar";
import { BulkEditEntriesDialog } from "./bulk-edit-entries-dialog";
import { ApprovalsFilterBar } from "./approvals-filter-bar";
import {
  applyApprovalFilters,
  deriveFilterOptions,
  emptyFilters,
  type ApprovalFilterState,
} from "./approvals-filter-bar.helpers";

type QueueStatus = "submitted" | "approved" | "rejected";

type QueueEntry = FunctionReturnType<
  typeof api.timesheets.entries.approvalQueue
>[number];

// Covers the full status union of the queue's rows. "draft" rows never
// reach the approvals queue, but the query's return type includes it.
const STATUS_META: Record<
  QueueEntry["status"],
  { label: string; color: string }
> = {
  draft: { label: "Draft", color: "gray" },
  submitted: { label: "Submitted", color: "blue" },
  approved: { label: "Approved", color: "emerald" },
  rejected: { label: "Rejected", color: "red" },
};

type GroupBy = "person" | "matter" | "category" | "date" | "none";

const GROUP_OPTIONS: { key: GroupBy; label: string }[] = [
  { key: "person", label: "Person" },
  { key: "matter", label: "Matter" },
  { key: "category", label: "Category" },
  { key: "date", label: "Date" },
  { key: "none", label: "No grouping" },
];

function ApprovalsHeading({ badges }: { badges?: ReactNode }) {
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-2xl font-semibold tracking-tight">
          Timesheet Approvals
        </h1>
        {badges}
      </div>
      <p className="text-sm text-muted-foreground">
        Review timesheets submitted for approval. Approving locks the entry and
        fixes rate; rejected entries go back to the submitter.
      </p>
    </div>
  );
}

function QueueCountBadges({
  entries,
  selectedCount,
}: {
  entries: QueueEntry[];
  selectedCount?: number;
}) {
  const awaiting = entries.filter((e) => e.status === "submitted").length;
  return (
    <div className="flex items-center gap-1">
      {awaiting > 0 && (
        <Badge size="sm" variant="soft" color="blue">
          {awaiting} awaiting
        </Badge>
      )}
      {(selectedCount ?? 0) > 0 && (
        <Badge size="sm" color="gray" variant="soft">
          {selectedCount} selected
        </Badge>
      )}
    </div>
  );
}

export function ApprovalsQueue({
  orgId,
  unitMinutes,
  roundingMode,
  jobId,
}: {
  orgId: Id<"organizations">;
  unitMinutes: number;
  /** MOR-1016 — firm's part-unit rounding direction for approver adjustments. */
  roundingMode: TimeRoundingMode;
  /** When set, the queue is scoped to a single matter (per-job tab). */
  jobId?: Id<"jobs">;
}) {
  // Status is a first-class filter (submitted by default) — approved and
  // rejected entries appear in the SAME table when opted in, not a
  // separate "recently approved" section.
  const [statusFilter, setStatusFilter] = useState<QueueStatus[]>([
    "submitted",
  ]);
  const { data: queue } = useQuery(
    convexQuery(api.timesheets.entries.approvalQueue, {
      orgId,
      statuses: statusFilter.length > 0 ? statusFilter : undefined,
      ...(jobId ? { jobId } : {}),
    }),
  );
  // Category visuals so badges here match the rest of the app.
  const { data: categories } = useQuery(
    convexQuery(api.timesheets.categories.listByOrg, { orgId }),
  );
  const approveEntries = useMutation(api.timesheets.entries.approveEntries);
  const rejectEntries = useMutation(api.timesheets.entries.rejectEntries);

  const [groupBy, setGroupBy] = useState<GroupBy>("person");
  const [filters, setFilters] = useState<ApprovalFilterState>(emptyFilters);
  const [selected, setSelected] = useState<Set<Id<"timeEntries">>>(new Set());
  const [rejectOpen, setRejectOpen] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [detailEntryId, setDetailEntryId] = useState<Id<"timeEntries"> | null>(
    null,
  );

  const entries = useMemo(() => queue ?? [], [queue]);

  const categoryVisual = useMemo(() => {
    const byId = new Map((categories ?? []).map((c) => [c._id as string, c]));
    return (categoryId: Id<"timeCategories"> | undefined) => {
      const c = categoryId ? byId.get(categoryId as string) : undefined;
      return { color: c?.color, icon: c?.icon, ipsCode: c?.ipsCode };
    };
  }, [categories]);

  const toHours = (units: number) =>
    ((units * unitMinutes) / 60).toLocaleString("en-AU", {
      maximumFractionDigits: 2,
    });
  const amountOf = (e: QueueEntry) =>
    e.hourlyRate !== undefined
      ? ((e.units * unitMinutes) / 60) * e.hourlyRate
      : undefined;

  // Options come from the loaded queue, so the bar only offers values that
  // can actually match.
  const filterOptions = useMemo(() => deriveFilterOptions(entries), [entries]);

  const filtered = useMemo(
    () => applyApprovalFilters(entries, filters),
    [entries, filters],
  );

  const groups = useMemo(() => {
    const keyFor = (e: QueueEntry): string => {
      switch (groupBy) {
        case "person":
          return e.userName;
        case "matter":
          return e.kind === "firm"
            ? "Firm (non-chargeable)"
            : e.jobCode
              ? `${e.jobCode} — ${e.jobName}`
              : e.jobName;
        case "category":
          return e.categoryName;
        case "date":
          return e.date;
        case "none":
          return "";
      }
    };
    const map = new Map<string, QueueEntry[]>();
    for (const e of filtered) {
      const key = keyFor(e);
      const list = map.get(key) ?? [];
      list.push(e);
      map.set(key, list);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [filtered, groupBy]);

  // Drop selections the filters have hidden. Without this, filtering to one
  // person and hitting "Approve selected" would also approve entries selected
  // before the filter was applied and no longer on screen.
  useEffect(() => {
    const visible = new Set(filtered.map((e) => e._id));
    setSelected((prev) => {
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [filtered]);

  const toggle = (id: Id<"timeEntries">) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  // Select-all covers what's on screen, never the whole unfiltered queue.
  const toggleAll = () => {
    setSelected((prev) =>
      prev.size === filtered.length
        ? new Set()
        : new Set(filtered.map((e) => e._id)),
    );
  };

  const selectedIds = [...selected];

  const handleApprove = async () => {
    setBusy(true);
    try {
      const count = await approveEntries({ entryIds: selectedIds });
      toast.success(
        count === 1 ? "1 entry approved." : `${count} entries approved.`,
      );
      setSelected(new Set());
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not approve the entries."));
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return;
    setBusy(true);
    try {
      const count = await rejectEntries({
        entryIds: selectedIds,
        reason: rejectReason.trim(),
      });
      toast.success(
        count === 1 ? "1 entry rejected." : `${count} entries rejected.`,
      );
      setSelected(new Set());
      setRejectOpen(false);
      setRejectReason("");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not reject the entries."));
    } finally {
      setBusy(false);
    }
  };

  if (queue === undefined) {
    return (
      <div className="flex w-full flex-col gap-5">
        <ApprovalsHeading />
        <div className="flex h-64 items-center justify-center">
          <Spinner className="text-muted-foreground" />
        </div>
      </div>
    );
  }

  const detailSheet = (
    <EntryDetailSheet
      entryId={detailEntryId}
      open={detailEntryId !== null}
      onOpenChange={(next) => {
        if (!next) setDetailEntryId(null);
      }}
      unitMinutes={unitMinutes}
      categoryVisual={categoryVisual}
    />
  );

  const submittedOnly =
    statusFilter.length === 1 && statusFilter[0] === "submitted";
  const approvedOnly =
    statusFilter.length === 1 && statusFilter[0] === "approved";
  const rejectedOnly =
    statusFilter.length === 1 && statusFilter[0] === "rejected";

  return (
    <div className="flex w-full flex-col gap-5">
      <ApprovalsHeading
        badges={
          <QueueCountBadges entries={filtered} selectedCount={selected.size} />
        }
      />
      <ApprovalScopeNote orgId={orgId} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <FacetedFilter
              title="Status"
              options={[
                { value: "submitted", label: "Submitted" },
                { value: "approved", label: "Approved" },
                { value: "rejected", label: "Rejected" },
              ]}
              selectedValues={statusFilter}
              onSelectionChange={(values) =>
                setStatusFilter(
                  values.length > 0 ? (values as QueueStatus[]) : ["submitted"],
                )
              }
            />
            <Label className="text-xs text-muted-foreground">Group by</Label>
            <Select
              value={groupBy}
              onValueChange={(value) => setGroupBy(value as GroupBy)}
            >
              <SelectTrigger className="h-8 w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GROUP_OPTIONS.filter(
                  (g) => !(jobId && g.key === "matter"),
                ).map((g) => (
                  <SelectItem key={g.key} value={g.key}>
                    {g.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {entries.length > 0 && (
        <ApprovalsFilterBar
          filters={filters}
          onChange={setFilters}
          options={filterOptions}
        />
      )}

      {entries.length === 0 ? (
        <div className="flex h-40 flex-col items-center justify-center gap-1 rounded-xl bg-background text-sm text-muted-foreground shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]">
          {submittedOnly ? (
            <>
              <Check className="size-6 text-emerald-600" />
              <p className="font-medium text-foreground">
                All caught up - no entries waiting for approval.
              </p>
              <p className="text-xs">
                Open Status and select Approved to see all entries previously
                approved.
              </p>
            </>
          ) : (
            <>
              <p className="font-medium text-foreground">
                {approvedOnly
                  ? "No approved timesheets"
                  : rejectedOnly
                    ? "No rejected timesheets"
                    : "No timesheets for these statuses"}
              </p>
              <p>
                {approvedOnly
                  ? "Approved entries will appear here once you have signed some off."
                  : rejectedOnly
                    ? "Rejected entries will appear here after you send some back."
                    : "Try a different Status combination."}
              </p>
            </>
          )}
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex h-40 flex-col items-center justify-center gap-1 rounded-xl bg-background text-sm text-muted-foreground shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]">
          <p className="font-medium text-foreground">No matching entries</p>
          <p>
            {entries.length === 1
              ? "The 1 entry doesn't match these filters."
              : `None of the ${entries.length} entries match these filters.`}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-2 h-8"
            onClick={() => setFilters(emptyFilters())}
          >
            Clear filters
          </Button>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={
                        selected.size === filtered.length && filtered.length > 0
                      }
                      onCheckedChange={toggleAll}
                      aria-label="Select all entries"
                    />
                  </TableHead>
                  <TableHead className="whitespace-nowrap">Date</TableHead>
                  <TableHead className="whitespace-nowrap">Person</TableHead>
                  <TableHead>Matter</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="min-w-64">Narrative</TableHead>
                  <TableHead className="text-right">Units</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Flags</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groups.map(([groupLabel, groupEntries]) => (
                  <GroupSection
                    key={groupLabel || "all"}
                    label={groupBy === "none" ? null : groupLabel}
                    groupBy={groupBy}
                    entries={groupEntries}
                    orgId={orgId}
                    selected={selected}
                    toggle={toggle}
                    toHours={toHours}
                    unitMinutes={unitMinutes}
                    roundingMode={roundingMode}
                    amountOf={amountOf}
                    categoryVisual={categoryVisual}
                    onOpenDetail={(id) => setDetailEntryId(id)}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject selected entries</DialogTitle>
            <DialogDescription>
              The reason is recorded in each entry's audit trail and notes, and
              shown to the team member so they can correct and resubmit.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label htmlFor="reject-reason">Reason</Label>
            <Textarea
              id="reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Narrative too vague — name the counterparty and document reviewed."
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!rejectReason.trim() || busy}
              onClick={() => void handleReject()}
            >
              {busy && <Loader2 className="size-4 animate-spin" />}
              Reject{" "}
              {selected.size === 1 ? "entry" : `${selected.size} entries`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BulkActionBar
        count={selected.size}
        onDone={() => setSelected(new Set())}
      >
        <BulkActionButton onClick={() => setBulkEditOpen(true)} disabled={busy}>
          <Pencil className="size-3.5" />
          Edit
        </BulkActionButton>
        <BulkActionButton
          tone="danger"
          onClick={() => setRejectOpen(true)}
          disabled={busy}
        >
          <X className="size-3.5" />
          Reject
        </BulkActionButton>
        <BulkActionButton onClick={() => void handleApprove()} disabled={busy}>
          {busy ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <Check className="size-3.5" />
          )}
          Approve
        </BulkActionButton>
      </BulkActionBar>

      <BulkEditEntriesDialog
        open={bulkEditOpen}
        onOpenChange={setBulkEditOpen}
        entryIds={selectedIds}
        categories={categories ?? []}
        onApplied={() => setSelected(new Set())}
      />

      {detailSheet}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Group section + rows
// ---------------------------------------------------------------------------

function GroupSection({
  label,
  groupBy,
  entries,
  orgId,
  selected,
  toggle,
  toHours,
  unitMinutes,
  roundingMode,
  amountOf,
  categoryVisual,
  onOpenDetail,
}: {
  label: string | null;
  groupBy: GroupBy;
  entries: QueueEntry[];
  orgId: Id<"organizations">;
  selected: Set<Id<"timeEntries">>;
  toggle: (id: Id<"timeEntries">) => void;
  toHours: (units: number) => string;
  unitMinutes: number;
  roundingMode: TimeRoundingMode;
  amountOf: (e: QueueEntry) => number | undefined;
  categoryVisual: (categoryId: Id<"timeCategories"> | undefined) => {
    color?: CategoryVisualColor;
    icon?: string;
    ipsCode?: string;
  };
  onOpenDetail: (id: Id<"timeEntries">) => void;
}) {
  const totalUnits = entries.reduce((sum, e) => sum + e.units, 0);
  return (
    <>
      {label !== null && (
        <TableRow className="bg-muted/50 hover:bg-muted/50">
          <TableCell colSpan={11} className="py-2">
            <span className="text-sm font-semibold">
              {groupBy === "date"
                ? format(parseISO(label), "EEEE d MMMM yyyy")
                : label}
            </span>
            <span className="ml-2 text-xs text-muted-foreground">
              {entries.length === 1 ? "1 entry" : `${entries.length} entries`} ·{" "}
              {toHours(totalUnits)}h
            </span>
          </TableCell>
        </TableRow>
      )}
      {entries.map((e) => (
        <QueueRow
          key={e._id}
          entry={e}
          orgId={orgId}
          selected={selected.has(e._id)}
          toggle={() => toggle(e._id)}
          toHours={toHours}
          unitMinutes={unitMinutes}
          roundingMode={roundingMode}
          amount={amountOf(e)}
          categoryVisual={categoryVisual}
          onOpenDetail={() => onOpenDetail(e._id)}
        />
      ))}
    </>
  );
}

type CategoryVisualColor = NonNullable<
  Parameters<typeof CategoryBadge>[0]["category"]["color"]
>;

/**
 * One submitted entry. The row opens the detail sheet; units and narrative
 * stay editable inline (§7.4 "adjust") with click-through suppressed on
 * the interactive cells.
 */
function QueueRow({
  entry,
  orgId,
  selected,
  toggle,
  toHours,
  unitMinutes,
  roundingMode,
  amount,
  categoryVisual,
  onOpenDetail,
}: {
  entry: QueueEntry;
  orgId: Id<"organizations">;
  selected: boolean;
  toggle: () => void;
  toHours: (units: number) => string;
  unitMinutes: number;
  roundingMode: TimeRoundingMode;
  amount: number | undefined;
  categoryVisual: (categoryId: Id<"timeCategories"> | undefined) => {
    color?: CategoryVisualColor;
    icon?: string;
    ipsCode?: string;
  };
  onOpenDetail: () => void;
}) {
  const adjustEntry = useMutation(api.timesheets.entries.adjustEntry);
  const [units, setUnits] = useState(String(entry.units));
  const [narrative, setNarrative] = useState(entry.narrative);

  useEffect(() => setUnits(String(entry.units)), [entry.units]);
  useEffect(() => setNarrative(entry.narrative), [entry.narrative]);

  const commit = (patch: { units?: number; narrative?: string }) => {
    adjustEntry({ entryId: entry._id, patch }).catch((err: unknown) => {
      toast.error(extractErrorMessage(err, "Could not adjust the entry."));
      setUnits(String(entry.units));
      setNarrative(entry.narrative);
    });
  };

  const stop = (event: MouseEvent) => event.stopPropagation();

  return (
    <TableRow className="cursor-pointer" onClick={onOpenDetail}>
      <TableCell onClick={stop}>
        {entry.status === "submitted" ? (
          <Checkbox
            checked={selected}
            onCheckedChange={toggle}
            aria-label={`Select entry on ${entry.date}`}
          />
        ) : null}
      </TableCell>
      <TableCell className="whitespace-nowrap text-sm tabular-nums">
        {format(parseISO(entry.date), "EEE d MMM")}
      </TableCell>
      <TableCell className="whitespace-nowrap text-sm">
        {entry.userName}
      </TableCell>
      <TableCell>
        <Badge variant="outline" color="gray" className="max-w-56 truncate">
          {entry.kind === "firm"
            ? "Firm activity"
            : `${entry.jobCode ? `${entry.jobCode} — ` : ""}${entry.jobName}`}
        </Badge>
      </TableCell>
      <TableCell onClick={stop}>
        <CategoryBadge
          category={{
            name: entry.categoryName,
            ...categoryVisual(entry.categoryId),
          }}
        />
      </TableCell>
      <TableCell className="min-w-64" onClick={stop}>
        <Input
          value={narrative}
          onChange={(e) => setNarrative(e.target.value)}
          onBlur={() => {
            const trimmed = narrative.trim();
            if (trimmed && trimmed !== entry.narrative) {
              commit({ narrative: trimmed });
            } else setNarrative(entry.narrative);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.currentTarget.blur();
          }}
          className="h-8 text-sm"
          aria-label="Adjust narrative"
          {...NARRATIVE_INPUT_PROPS}
        />
        <RateWorkingNote working={entry} labelled className="mt-1" />
        <NarrativeWarningNote
          narrative={narrative}
          units={Number(units)}
          unitMinutes={unitMinutes}
          orgId={orgId}
          className="mt-1"
        />
      </TableCell>
      <TableCell className="text-right" onClick={stop}>
        <div className="flex items-center justify-end gap-1">
          <Input
            type="number"
            min={1}
            step={1}
            value={units}
            onChange={(e) => setUnits(e.target.value)}
            onBlur={() => {
              const value = roundUnits(Number(units), roundingMode);
              if (
                Number.isFinite(value) &&
                value >= 1 &&
                value !== entry.units
              ) {
                commit({ units: value });
              } else setUnits(String(entry.units));
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
            }}
            className="h-8 w-16 text-right tabular-nums"
            aria-label="Adjust units"
          />
          <span className="w-10 text-xs tabular-nums text-muted-foreground">
            {toHours(entry.units)}h
          </span>
        </div>
      </TableCell>
      <TableCell className="text-right text-sm tabular-nums">
        {entry.hourlyRate !== undefined ? formatAud(entry.hourlyRate) : "—"}
      </TableCell>
      <TableCell className="text-right text-sm tabular-nums">
        {amount !== undefined ? formatAudExact(amount) : "—"}
      </TableCell>
      <TableCell onClick={stop}>
        <div className="flex items-center gap-1.5">
          <Badge
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            color={STATUS_META[entry.status].color as any}
            variant="soft"
          >
            {STATUS_META[entry.status].label}
          </Badge>
          {entry.status === "approved" && entry.writtenOff && (
            <Badge className="bg-muted text-muted-foreground">
              Written off
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex gap-1">
          {!entry.billable && (
            <Badge className="bg-muted text-muted-foreground">
              Non-billable
            </Badge>
          )}
          {entry.projectCode === "pre" && (
            <Badge className="bg-muted text-muted-foreground">PRE</Badge>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}
